import { Box, ButtonBase, Tooltip } from '@mui/material';
import { ChevronRightOutlined } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { LocalizedString } from '@synergy/models';
import {
    PropsWithChildren,
    ReactElement,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
} from 'react';
import { converter } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useLocalizedContext } from '@synergy/components';

import { PaloSegmentedToggle } from './palo-segmented-toggle';
import { paloPlainText, paloPrintedPosition } from './palo-print-length';

const c = tokens.color;

/** How much of an article's description the position tables show. */
export type PaloDescriptionMode = 'off' | 'clamp' | 'full';

const STORAGE_KEY = 'palo.positions.descriptionMode';
const DEFAULT_MODE: PaloDescriptionMode = 'clamp';

const readStoredMode = (): PaloDescriptionMode => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);

        return 'off' === stored || 'clamp' === stored || 'full' === stored ? stored : DEFAULT_MODE;
    } catch {
        return DEFAULT_MODE;
    }
};

/*
 * One choice for every table on every document: the user picks it once and
 * finds it again on the next document, and an inquiry's customer cards each
 * hold a table of their own (#2257). Module state rather than a provider, so
 * the switch can stand in a section's heading, outside the table it governs.
 */
let state = { mode: readStoredMode(), allOpen: false };
const listeners = new Set<() => void>();

const update = (next: Partial<typeof state>): void => {
    state = { ...state, ...next };
    listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

const usePaloDescriptionState = () => useSyncExternalStore(subscribe, () => state);

const setMode = (mode: PaloDescriptionMode): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
        // No storage in a private window: the choice lasts for this page.
    }
    update({ mode, allOpen: false });
};

/** What a single table knows: which of its rows were opened or closed by hand. */
interface PaloRowDescriptionState {
    /** By the table's row key; the switch "Alle aufklappen" applies where nothing is set. */
    overrides: Map<string | number, boolean>;
    keyOf: (index: number) => string | number;
    toggle: (index: number, open: boolean) => void;
}

const PaloRowDescriptionContext = createContext<PaloRowDescriptionState | null>(null);

/** Held by PaloPositionTable for a mask that shows descriptions. */
export const PaloRowDescriptionProvider = ({
    keyOf,
    children,
}: PropsWithChildren<{ keyOf: (index: number) => string | number }>): ReactElement => {
    const { mode, allOpen } = usePaloDescriptionState();
    const [overrides, setOverrides] = useState<Map<string | number, boolean>>(new Map());

    // "Alle aufklappen" and a new choice of mode speak for every row again.
    useEffect(() => setOverrides(new Map()), [mode, allOpen]);

    const value = useMemo<PaloRowDescriptionState>(
        () => ({
            overrides,
            keyOf,
            toggle: (index, open) => setOverrides(current => new Map(current).set(keyOf(index), open)),
        }),
        [overrides, keyOf]
    );

    return <PaloRowDescriptionContext.Provider value={value}>{children}</PaloRowDescriptionContext.Provider>;
};

const useRowOpen = (index: number): { open: boolean; toggle: () => void } | null => {
    const { mode, allOpen } = usePaloDescriptionState();
    const rows = useContext(PaloRowDescriptionContext);

    if (!rows) {
        return null;
    }

    const own = rows.overrides.get(rows.keyOf(index));
    const open = 'full' === mode || (undefined === own ? allOpen : own);

    return { open, toggle: () => rows.toggle(index, !open) };
};

/** "Beschreibung  Aus | 1 Zeile | Ganz" and "Alle aufklappen", for a section's heading. */
export const PaloDescriptionModeToggle = (): ReactElement => {
    const { mode, allOpen } = usePaloDescriptionState();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '11.5px',
                fontWeight: 400,
                letterSpacing: 'normal',
                textTransform: 'none',
                color: c.ink2,
            }}
        >
            <FormattedMessage id="app.positions.description" defaultMessage="Beschreibung" />
            <PaloSegmentedToggle
                size="small"
                value={mode}
                options={[
                    {
                        value: 'off',
                        label: <FormattedMessage id="app.positions.description.off" defaultMessage="Aus" />,
                    },
                    {
                        value: 'clamp',
                        label: <FormattedMessage id="app.positions.description.clamp" defaultMessage="1 Zeile" />,
                    },
                    {
                        value: 'full',
                        label: <FormattedMessage id="app.positions.description.full" defaultMessage="Ganz" />,
                    },
                ]}
                onChange={value => setMode(value as PaloDescriptionMode)}
            />
            {'full' !== mode && (
                <ButtonBase
                    onClick={() => update({ allOpen: !allOpen })}
                    sx={{ color: c.accentHi, fontSize: '11.5px', '&:hover': { textDecoration: 'underline' } }}
                >
                    {allOpen ? (
                        <FormattedMessage id="app.positions.description.closeAll" defaultMessage="Alle zuklappen" />
                    ) : (
                        <FormattedMessage id="app.positions.description.openAll" defaultMessage="Alle aufklappen" />
                    )}
                </ButtonBase>
            )}
        </Box>
    );
};

/** The width of the chevron, held free where there is none so the numbers stay in line. */
export const PALO_DESCRIPTION_CHEVRON_WIDTH = 16;

/** ▸ / ▾ in front of the position number. */
export const PaloDescriptionChevron = ({ index, has }: { index: number; has: boolean }): ReactElement => {
    const { mode } = usePaloDescriptionState();
    const row = useRowOpen(index);
    const blank = <Box component="span" sx={{ width: PALO_DESCRIPTION_CHEVRON_WIDTH, flexShrink: 0 }} />;

    if (!has || !row || 'full' === mode) {
        return blank;
    }

    return (
        <ButtonBase
            draggable={false}
            // The number cell is the handle a row is dragged by; opening the
            // text must not pick the row up.
            onMouseDown={event => event.stopPropagation()}
            onClick={event => {
                event.stopPropagation();
                row.toggle();
            }}
            aria-expanded={row.open}
            aria-label={row.open ? 'Beschreibung zuklappen' : 'Beschreibung aufklappen'}
            sx={{
                width: PALO_DESCRIPTION_CHEVRON_WIDTH,
                flexShrink: 0,
                color: c.ink3,
                borderRadius: '3px',
                '&:hover': { color: c.ink, backgroundColor: c.neutralSoft },
            }}
        >
            <ChevronRightOutlined
                sx={{ fontSize: 16, transition: 'transform .12s', transform: row.open ? 'rotate(90deg)' : 'none' }}
            />
        </ButtonBase>
    );
};

export interface PaloPositionDescriptionProps {
    /** The row's place in the document, as the table hands it to the mask. */
    index: number;
    shortDescription?: LocalizedString | string | null;
    longDescription?: LocalizedString | string | null;
    /** What the PDF cuts a position text at; left out, no count is shown. */
    printLimit?: number;
    /** Opens the position's own dialog. Left out on a document that is through. */
    onEdit?: () => void;
}

/** Whether a position has any text to show under its name. */
export const paloHasDescription = (
    getLocalized: (value?: LocalizedString) => string,
    ...texts: (LocalizedString | string | null | undefined)[]
): boolean => texts.some(text => '' !== paloPlainText(resolve(getLocalized, text)));

const resolve = (getLocalized: (value?: LocalizedString) => string, text?: LocalizedString | string | null): string =>
    null == text ? '' : 'string' === typeof text ? text : getLocalized(text);

/**
 * Under the article's name: what the article is.
 *
 * #2208 took the description out of the rows because five lines of it made
 * the table uneven, and the clerks then opened every position's dialog to
 * read it. Two lines of plain text keep the rows even and say enough to know
 * the article; a row opens to the whole text with its formatting and with
 * what the PDF will print of it (#2257).
 */
export const PaloPositionDescription = ({
    index,
    shortDescription,
    longDescription,
    printLimit,
    onEdit,
}: PaloPositionDescriptionProps): ReactElement | null => {
    const { mode } = usePaloDescriptionState();
    const row = useRowOpen(index);
    const localized = useLocalizedContext();
    const short = resolve(localized.getLocalizedString, shortDescription).trim();
    const long = resolve(localized.getLocalizedString, longDescription).trim();
    // The long text says more; the short one stands in where there is none.
    const shown = long || short;

    if (!row || '' === paloPlainText(shown)) {
        return null;
    }

    if (row.open) {
        const printed = paloPrintedPosition(short, long);
        const over = undefined !== printLimit && printed.length > printLimit;

        return (
            <Box
                sx={{
                    mt: '4px',
                    mb: '2px',
                    padding: '6px 10px',
                    backgroundColor: c.surfaceAlt,
                    borderLeft: `3px solid ${c.accentLine}`,
                    borderRadius: '0 4px 4px 0',
                    fontSize: '12px',
                    lineHeight: 1.45,
                    color: c.ink2,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                }}
            >
                <Box
                    sx={{ '& p, & ul, & ol': { margin: 0 }, '& ul, & ol': { paddingLeft: '18px' } }}
                    dangerouslySetInnerHTML={{ __html: converter.makeHtml(shown) }}
                />
                <Box
                    sx={{
                        mt: '5px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px 14px',
                        fontSize: '11px',
                        color: c.ink3,
                    }}
                >
                    <span>
                        <FormattedMessage id="app.positions.description.inPdf" defaultMessage="Im PDF:" />{' '}
                        <Box component="b" sx={{ color: c.ink2 }}>
                            {printed.isShort ? (
                                <FormattedMessage
                                    id="app.positions.description.short"
                                    defaultMessage="Kurzbeschreibung"
                                />
                            ) : (
                                <FormattedMessage
                                    id="app.positions.description.long"
                                    defaultMessage="Artikelbeschreibung"
                                />
                            )}
                        </Box>
                        {undefined !== printLimit && (
                            <>
                                {' · '}
                                <Box component="span" sx={over ? { color: c.crit, fontWeight: 600 } : undefined}>
                                    <FormattedMessage
                                        id="app.common.characterLimit"
                                        values={{ value: printed.length, total: printLimit }}
                                    />
                                </Box>
                            </>
                        )}
                    </span>
                    {!printed.isShort && (
                        <span>
                            <FormattedMessage id="app.positions.description.short" defaultMessage="Kurzbeschreibung" />
                            {': –'}
                        </span>
                    )}
                    {onEdit && (
                        <ButtonBase
                            onClick={onEdit}
                            sx={{ color: c.accentHi, fontSize: '11px', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {'✎ '}
                            <FormattedMessage id="app.common.edit" defaultMessage="Bearbeiten" />
                        </ButtonBase>
                    )}
                </Box>
            </Box>
        );
    }

    if ('off' === mode) {
        return null;
    }

    // Plain text in one line, cut with an ellipsis (GF 19.10.2026): the marks
    // and breaks of the formatting would make the rows uneven again.
    const plain = paloPlainText(shown);

    return (
        <Tooltip title={plain} placement="bottom-start" enterDelay={400}>
            <Box
                sx={{
                    mt: '1px',
                    fontSize: '11.5px',
                    lineHeight: 1.4,
                    color: c.ink3,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {plain}
            </Box>
        </Tooltip>
    );
};
